import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { backendConfig } from '../config/backendConfig';
import { authFetch } from '../utils/authFetch';

const FEATURE_KEYS = [
  'artHistoryAppreciation',
  'styleTransfer',
  'dailyActivities',
  'individualArtAppreciation',
  'storyboarding',
];

export function useFeatureQuota() {
  const { user, membership } = useAuth();
  const [usage, setUsage] = useState({});
  const [featureLimits, setFeatureLimits] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [carryForward, setCarryForward] = useState({});
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalFeature, setUpgradeModalFeature] = useState(null);

  const loadUsage = useCallback(async () => {
    if (!user?.uid) {
      setUsage({});
      setFeatureLimits(null);
      setPeriodEnd(null);
      setCarryForward({});
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const baseUrl = backendConfig.baseUrl || '';
      const apiBase = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
      const res = await authFetch(`${apiBase}/membership/usage`);
      if (res.ok) {
        const data = await res.json();
        const membershipStatus = membership?.membership?.status;
        const membershipIsActive = membershipStatus === 'active' || membershipStatus === 'trialing';
        const membershipPeriodEnd =
          membership?.expiresAt ||
          membership?.membership?.currentPeriodEnd ||
          membership?.membership?.trialEndsAt ||
          membership?.membership?.expiresAt ||
          null;
        // Keep quota display aligned with membership/status source of truth.
        // If membership is active and has featureLimits, prefer those over /usage.
        const resolvedFeatureLimits = (membershipIsActive && membership?.featureLimits)
          ? membership.featureLimits
          : (data.featureLimits || membership?.featureLimits || null);
        setUsage(data.usage || {});
        setFeatureLimits(resolvedFeatureLimits);
        setPeriodEnd(data.periodEnd ?? membershipPeriodEnd ?? null);
        setCarryForward(data.carryForward || {});
      } else {
        setFeatureLimits(membership?.featureLimits || null);
        setPeriodEnd(
          membership?.expiresAt ||
          membership?.membership?.currentPeriodEnd ||
          membership?.membership?.trialEndsAt ||
          membership?.membership?.expiresAt ||
          null
        );
        setCarryForward({});
      }
    } catch (e) {
      setFeatureLimits(membership?.featureLimits || null);
      setPeriodEnd(
        membership?.expiresAt ||
        membership?.membership?.currentPeriodEnd ||
        membership?.membership?.trialEndsAt ||
        membership?.membership?.expiresAt ||
        null
      );
      setCarryForward({});
    } finally {
      setLoading(false);
    }
  }, [user?.uid, membership]);

  useEffect(() => {
    if (membership?.featureLimits && !usage?.artHistoryAppreciation && usage?.artHistoryAppreciation !== 0) {
      setFeatureLimits(membership.featureLimits);
    }
  }, [membership?.featureLimits, usage]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  // Production-safe source of truth: only use server-provided limits
  // (usage endpoint or membership/status endpoint). No hardcoded fallback.
  const limits = featureLimits || membership?.featureLimits || null;
  const planKey = membership?.membership?.status === 'trialing' ? 'artmocode' : (membership?.membership?.planId || 'basic');

  const getLimit = (feature) => {
    // Important: before membership/usage resolves, do NOT treat missing limits as over-quota.
    // Returning disabled here causes false "upgrade" popups on fresh login/local startup.
    if (!limits) return { enabled: true, limit: 'unlimited', unresolved: true };
    const config = limits?.[feature];
    if (!config || !config.enabled) return { enabled: false, limit: 0, unresolved: false };
    const effectiveLimit = config.effectiveLimit !== undefined && config.effectiveLimit !== null ? config.effectiveLimit : config.limit;
    return { enabled: true, limit: effectiveLimit, unresolved: false };
  };

  const canUseFeature = useCallback((feature) => {
    const { enabled, limit } = getLimit(feature);
    if (!enabled) return false;
    if (limit === 'unlimited') return true;
    const count = usage[feature] || 0;
    return count < (limit || 0);
  }, [limits, usage]);

  const checkQuotaBeforeUse = useCallback((feature) => {
    const { enabled, limit, unresolved } = getLimit(feature);
    if (unresolved) {
      // Avoid false negatives while quota data is still loading.
      return { allowed: true, overLimit: false };
    }
    if (!enabled) {
      setShowUpgradeModal(true);
      setUpgradeModalFeature(feature);
      return { allowed: false, overLimit: true };
    }
    if (limit === 'unlimited') return { allowed: true, overLimit: false };
    const count = usage[feature] || 0;
    if (count >= (limit || 0)) {
      setShowUpgradeModal(true);
      setUpgradeModalFeature(feature);
      return { allowed: false, overLimit: true };
    }
    return { allowed: true, overLimit: false };
  }, [limits, usage]);

  const recordUsage = useCallback(async (feature, styleId, artworkId) => {
    if (!user?.uid) return { ok: false, overLimit: false };
    try {
      const baseUrl = backendConfig.baseUrl || '';
      const apiBase = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
      let body = { feature };
      if (feature === 'styleTransfer' && styleId != null) {
        body = { feature, styleId: String(styleId).trim() };
        if (artworkId != null) body.artworkId = String(artworkId).trim();
      } else if (feature === 'artHistoryAppreciation' && artworkId != null) {
        body = { feature, artworkId: String(artworkId).trim() };
      }
      const res = await authFetch(`${apiBase}/membership/usage/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.usage) {
        setUsage(data.usage);
        if (data.carryForward) setCarryForward(data.carryForward);
        if (data.featureLimits) setFeatureLimits(data.featureLimits);
        if (data.overLimit) {
          setShowUpgradeModal(true);
          setUpgradeModalFeature(feature);
        }
        return { ok: true, overLimit: !!data.overLimit };
      }
      if (res.status === 403 && data.overLimit) {
        setShowUpgradeModal(true);
        setUpgradeModalFeature(feature);
        return { ok: false, overLimit: true };
      }
      return { ok: res.ok, overLimit: false };
    } catch (e) {
      return { ok: false, overLimit: false };
    }
  }, [user?.uid]);

  const checkAndRecordUsage = useCallback(async (feature) => {
    const check = checkQuotaBeforeUse(feature);
    if (!check.allowed) return check;
    const result = await recordUsage(feature);
    return { allowed: result.ok && !result.overLimit, overLimit: result.overLimit };
  }, [checkQuotaBeforeUse, recordUsage]);

  const dismissUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
    setUpgradeModalFeature(null);
  }, []);

  return {
    usage,
    featureLimits: limits,
    periodEnd,
    carryForward,
    loading,
    canUseFeature,
    checkQuotaBeforeUse,
    recordUsage,
    checkAndRecordUsage,
    showUpgradeModal,
    upgradeModalFeature,
    setShowUpgradeModal,
    dismissUpgradeModal,
    refreshUsage: loadUsage,
  };
}
